import styled from "styled-components";
import {baseDistance, ELLIPSIS, FlexCenterStyle, fontSize, fontWeight, radius, VerticalFlexStyle} from "../../styles/styles";
import {colorPalette} from "../../styles/colorPalette";
import {TabViewActionBarStyled, TabViewButtonContainerStyled} from "../../widgets/tab/TabViewV5";
import {IconButtonStyled} from "../../interaction/IconButtonV5";
import {TabViewStyled} from "../../widgets/tab/TabView.styled";
import {ToggleButton} from "../../widgets/ToggleButton.styled";
import {ToggleContainerStyled} from "../../widgets/ToggleContainerV5/ToggleContainerV5";
import {TabChildStyled} from "./TabChildV5";

export const ColumnContainerV5Styled = styled.div<{height?: string}>`
	${VerticalFlexStyle};
	gap: ${baseDistance.sm};
	min-height: 0;
	overflow-y: auto;
	position: relative;
	flex: 1;

	& > div {
		// this div is the actual drop area by the library itself (hello-pangea/dnd)
		&:only-child {
			flex: 1;
		}

		&:last-child {
			.column:last-child {
				margin-bottom: ${baseDistance.sm};
			}
		}
	}
`;

export const MoveButtonsStyled = styled.div`
	${FlexCenterStyle};
	visibility: hidden;

	${IconButtonStyled} {
		margin-right: 0;
		width: 24px;
		min-width: initial;
		height: 24px;

		svg {
			height: 16px;
			width: 16px;
		}

		&:hover {
			cursor: pointer;
			border: 2px solid ${colorPalette.gray.c700Dark};
		}
	}
`;

export const ColumnEditorStyled = styled.div`
	border: 1px solid ${colorPalette.gray.c300};
	border-radius: ${baseDistance.xs};
	${VerticalFlexStyle};
	min-height: 0;
	flex: 1;

	.dropArea {
		display: flex;
		border: 3px dashed ${colorPalette.libraryColors.explosive};
		margin: ${baseDistance.sm};
		flex: 1;
		overflow: hidden;

		.droparea-context {
			display: flex;
			align-items: center;
			justify-content: center;
			flex: 1;
			color: ${colorPalette.gray.c400};

			> span {
				position: absolute;

				& + div {
					position: absolute;
				}
			}
		}
	}

	.SearchField {
		background: ${colorPalette.white};
		width: auto;
		border: 1px solid ${colorPalette.gray.c300};
		height: 32px;
	}

	.ToggleContainer:not(.FieldSection) {
		border: none;
	}

	.ToggleContainer .children.open {
		padding: 10px 0;
	}

	.column {
		cursor: pointer;
		height: 34px;
		display: flex;
		align-items: center;
		font-style: normal;
		font-weight: ${fontWeight.normal};
		font-size: ${fontSize.md};
		line-height: ${baseDistance.md};
		color: ${colorPalette.gray.c950};
		position: relative;
		padding: ${baseDistance.xs};
		border-radius: ${radius.sm};

		&.semiHidden {
			opacity: 0.5;
		}

		.name {
			${ELLIPSIS}
			line-height: 18px;
			min-width: 0;
			flex: 1;
		}

		> svg {
			width: 16px;
			height: 16px;
			margin-right: ${baseDistance.xs};
		}

		.drag {
			color: ${colorPalette.gray.c700Dark};
		}

		&:not(.disabled) {
			cursor: grab;
			position: relative;

			&:hover {
				background: ${colorPalette.gray.c200Light};
			}
			&.selected {
				background: ${colorPalette.primary.c200Light};

				.name {
					color: ${colorPalette.primary.c500Primary};
				}

				svg:not(.drag) {
					color: ${colorPalette.primary.c500Primary};
				}
			}
		}

		&.disabled {
			background: ${colorPalette.white};
			pointer-events: none;
			color: ${colorPalette.libraryColors.explosive};
			border: 1px solid #f0f0f0;
		}

		// count column is a special blue column in Report
		&.count {
			background: ${colorPalette.primary.c200Light};
		}

		.selectionCount {
			position: absolute;
			color: ${colorPalette.primary.c500Primary};
			background: ${colorPalette.white};
			border-radius: 50%;
			width: 25px;
			height: 25px;
			text-align: center;
			border: 1px solid ${colorPalette.primary.c500Primary};
			font-size: 15px;
			font-weight: ${fontWeight.bold};
			line-height: 23px;
			top: -7px;
			right: -5px;
		}

		&:hover {
			${MoveButtonsStyled} {
				visibility: visible;
			}
		}
	}

	.manage-cols-box {
		display: flex;
		min-height: 0;
		flex: 1;

		.col {
			${VerticalFlexStyle};
			flex: 1;
			min-height: 0;
			padding: ${baseDistance.sm};
			padding-bottom: 0;
			gap: ${baseDistance.sm};
			width: 50%;

			.rightHeader {
				color: ${colorPalette.gray.c950};
				font-weight: ${fontWeight.bold};
				font-size: ${fontSize.md};
				display: flex;
				line-height: ${baseDistance.md};
			}

			.button {
				color: ${colorPalette.gray.c700Dark};

				&.disabled {
					opacity: 0.5;
				}
			}

			.FindInList {
				.button {
					width: auto;
					height: auto;
				}
			}

			${TabViewStyled} {
				${VerticalFlexStyle};
				flex: 1;
				min-height: 0;
				gap: ${baseDistance.sm};

				.views {
					${VerticalFlexStyle};
					min-height: 0;
					flex: 1;

					${TabChildStyled} {
						gap: ${baseDistance.sm};
						flex: 1;
					}
				}

				${TabViewButtonContainerStyled} {
					padding: 0;

					${TabViewActionBarStyled} {
						justify-content: space-around;
						gap: ${baseDistance.sm};
						max-height: 16px;
						min-height: auto;

						&:has(${ToggleButton}:only-child) {
							justify-content: left;

							${ToggleButton} {
								cursor: unset;
								pointer-events: none;
							}
						}
					}
				}
			}

			&.linkedFields {
				.TabView {
					& > .buttons {
						display: flex;
					}
				}
			}

			${ToggleContainerStyled} {
				flex: 1;
				margin-bottom: 0;

				&.prohibited {
					background: #ffb4b4;
				}

				.filter-title {
					color: ${colorPalette.gray.c950};
					font-size: ${fontSize.sm};
					font-weight: ${fontWeight.bold};
					line-height: 16px;
					display: flex;
					align-items: center;
					letter-spacing: 1%;

					svg {
						width: 16px;
						height: 16px;
					}
				}

				.children.open {
					padding: 0;
					margin-bottom: 0px;
				}
			}
		}
	}

	user-select: none;

	.TabView {
		.views {
			.DetailsContent {
				overflow: auto;
			}
		}
	}
`;
